/** Order status maps, CSV mapping, and progression helpers */

export const regularStatusMapping = {
  'unfulfilled': 'Order Placed',
  'packing': 'Packing Started',
  'shipping_domestic': 'Shipped Domestic',
  'shipping_international': 'Shipped International',
  'arrived_at_destination': 'Arrived in Destination Country',
  'out_of_delivery': 'Out for Delivery',
  'destoryed': 'Customs Cleared',
  'delivered': 'Delivered',
  'review': 'Review Request',
  'dispute': 'Issue Reported',
  'replacement_approved': 'Replacement Approved',
  'replacement_packing': 'Replacement Packed',
  'replacement_shipping_domestic': 'Replacement Shipped Domestic',
  'replacement_shipping_international': 'Replacement Shipped International',
  'replacement_delivered': 'Replacement Delivered',
  'refunded': 'Refund Processed'
};

export const preorderStatusMapping = {
  'preorder_received': 'Preorder Placed',
  'progress': 'Production Update',
  'delay': 'Delay Update',
  'stock_in': 'Stock Arrived',
  'packing': 'Packing Started',
  'shipping_domestic': 'Shipped Domestic',
  'shipping_international': 'Shipped International',
  'arrived_at_destination': 'Arrived in Destination Country',
  'out_of_delivery': 'Out for Delivery',
  'destoryed': 'Customs Cleared',
  'delivered': 'Delivered',
  'review': 'Review Request',
  'dispute': 'Issue Reported',
  'replacement_approved': 'Replacement Approved',
  'replacement_packing': 'Replacement Packed',
  'replacement_shipping_domestic': 'Replacement Shipped Domestic',
  'replacement_shipping_international': 'Replacement Shipped International',
  'replacement_delivered': 'Replacement Delivered',
  'refunded': 'Refund Processed'
};

// Status progression (forward only + dispute resolution)
export const regularStatusProgression = ['unfulfilled', 'packing', 'shipping_domestic', 'shipping_international', 'arrived_at_destination', 'out_of_delivery', 'destoryed', 'delivered', 'review', 'dispute', 'replacement_approved', 'replacement_packing', 'replacement_shipping_domestic', 'replacement_shipping_international', 'replacement_delivered', 'refunded'];
export const preorderStatusProgression = ['preorder_received', 'progress', 'delay', 'stock_in', 'packing', 'shipping_domestic', 'shipping_international', 'arrived_at_destination', 'out_of_delivery', 'destoryed', 'delivered', 'review', 'dispute', 'replacement_approved', 'replacement_packing', 'replacement_shipping_domestic', 'replacement_shipping_international', 'replacement_delivered', 'refunded'];

// Get appropriate status mapping and progression based on order type
export const getStatusMapping = (isPreorder) => isPreorder ? preorderStatusMapping : regularStatusMapping;
export const getStatusProgression = (isPreorder) => isPreorder ? preorderStatusProgression : regularStatusProgression;

// Legacy mapping for backward compatibility
export const customStatusMap = regularStatusMapping;

// Map CSV STATUS column → system status key (accepts codes + old/new UI labels)
export const mapCsvStatusToSystem = (rawStatus) => {
  const csvStatus = String(rawStatus || '').trim().toLowerCase();
  if (!csvStatus) return '';

  const normalized = csvStatus.replace(/[\s-]+/g, '_');
  const exact = {
    unfulfilled: 'unfulfilled',
    order_received: 'unfulfilled',
    order_placed: 'unfulfilled',
    pending: 'unfulfilled',
    preorder_received: 'preorder_received',
    preorder_confirmed: 'preorder_received',
    preorder_placed: 'preorder_received',
    progress: 'progress',
    production_update: 'progress',
    production: 'progress',
    delay: 'delay',
    delay_update: 'delay',
    preorder_delay: 'delay',
    stock_in: 'stock_in',
    stock_arrived: 'stock_in',
    packing: 'packing',
    packing_started: 'packing',
    shipping_domestic: 'shipping_domestic',
    shipped_domestic: 'shipping_domestic',
    shipping_international: 'shipping_international',
    shipped_international: 'shipping_international',
    arrived_at_destination: 'arrived_at_destination',
    arrived_in_destination_country: 'arrived_at_destination',
    out_of_delivery: 'out_of_delivery',
    out_for_delivery: 'out_of_delivery',
    destoryed: 'destoryed',
    destroyed: 'destoryed',
    destroy: 'destoryed',
    customs_cleared: 'destoryed',
    delivered: 'delivered',
    review: 'review',
    review_request: 'review',
    review_product: 'review',
    dispute: 'dispute',
    issue_reported: 'dispute',
    replacement_approved: 'replacement_approved',
    replacement_packing: 'replacement_packing',
    replacement_packed: 'replacement_packing',
    replacement_shipping_domestic: 'replacement_shipping_domestic',
    replacement_shipped_domestic: 'replacement_shipping_domestic',
    replacement_shipping_international: 'replacement_shipping_international',
    replacement_shipped_international: 'replacement_shipping_international',
    replacement_delivered: 'replacement_delivered',
    refunded: 'refunded',
    refund_processed: 'refunded',
  };
  if (exact[normalized]) return exact[normalized];

  // Keyword fallbacks (order matters — more specific first)
  if (csvStatus.includes('replacement') && csvStatus.includes('deliver')) return 'replacement_delivered';
  if (csvStatus.includes('replacement') && (csvStatus.includes('ship') || csvStatus.includes('shipping'))) {
    if (csvStatus.includes('international') || csvStatus.includes('intl')) return 'replacement_shipping_international';
    return 'replacement_shipping_domestic';
  }
  if (csvStatus.includes('replacement') && csvStatus.includes('pack')) return 'replacement_packing';
  if (csvStatus.includes('replacement') && csvStatus.includes('approv')) return 'replacement_approved';
  if (csvStatus.includes('preorder') && (csvStatus.includes('confirm') || csvStatus.includes('receiv') || csvStatus.includes('place'))) return 'preorder_received';
  if (csvStatus.includes('delay')) return 'delay';
  if (csvStatus.includes('production') || csvStatus === 'progress') return 'progress';
  if (csvStatus.includes('customs') && !csvStatus.includes('customer')) return 'destoryed';
  if (csvStatus.includes('destory') || csvStatus.includes('destroy')) return 'destoryed';
  if (csvStatus.includes('arrived') && csvStatus.includes('destination')) return 'arrived_at_destination';
  if (csvStatus.includes('out for') || csvStatus.includes('out of delivery')) return 'out_of_delivery';
  if (csvStatus.includes('issue') || csvStatus.includes('dispute')) return 'dispute';
  if (csvStatus.includes('review')) return 'review';
  if (csvStatus.includes('refund')) return 'refunded';
  if (csvStatus.includes('stock')) return 'stock_in';
  if (csvStatus.includes('pack')) return 'packing';
  if (csvStatus.includes('ship')) {
    if (csvStatus.includes('international') || csvStatus.includes('intl')) return 'shipping_international';
    return 'shipping_domestic';
  }
  if (csvStatus.includes('deliver')) return 'delivered';
  if (csvStatus.includes('receive') || csvStatus.includes('pending') || (csvStatus.includes('order') && csvStatus.includes('place'))) return 'unfulfilled';
  return '';
};

// Get status index for comparison based on order type
export const getStatusIndex = (status, isPreorder = false) => {
  const progression = getStatusProgression(isPreorder);
  return progression.indexOf(status);
};

export const isReplacementStatus = (status) => [
  'replacement_approved',
  'replacement_packing',
  'replacement_shipping_domestic',
  'replacement_shipping_international',
  'replacement_delivered'
].includes(status);

// Check if status change is allowed (forward only + dispute special cases)
export const isStatusChangeAllowed = (currentStatus, newStatus, isPreorder = false) => {
  const progression = getStatusProgression(isPreorder);
  const currentIndex = progression.indexOf(currentStatus);
  const newIndex = progression.indexOf(newStatus);
  
  // Special case: From dispute, allow only refund or replacement approval.
  if (currentStatus === 'dispute') {
    return newStatus === 'refunded' || newStatus === 'replacement_approved';
  }
  
  // Normal case: Only allow forward or same status
  return newIndex >= currentIndex;
};

